import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  Trash2,
  Download,
  Mail,
  Phone,
  Calendar,
  MessageSquare,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "lsf_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!password || loggingIn) return;
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      const t = data.token || password;
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      toast.success("Welcome — logged in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password.");
    } finally {
      setLoggingIn(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setPassword("");
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6" data-testid="admin-login-page">
        <form
          onSubmit={onLogin}
          className="w-full max-w-sm bg-white border border-[#E0DCD3] rounded-sm p-8"
          data-testid="admin-login-form"
        >
          <p className="overline mb-4">Admin · Loughrea Solar Farm</p>
          <h1 className="font-serif-display text-2xl tracking-tight mb-6">Sign in to manage bookings</h1>
          <label className="block">
            <span className="block text-[12px] font-medium text-[#4C4538] mb-1.5">Password</span>
            <input
              type="password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password"
              className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
            />
          </label>
          <button
            type="submit"
            disabled={loggingIn || !password}
            data-testid="admin-login-submit"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 h-11 px-6 rounded-sm bg-[#3A4B3A] text-white text-sm font-medium hover:bg-[#2A3B2A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {loggingIn ? "Signing in…" : "Sign in"}
          </button>
          <a href="/" className="block text-center mt-5 text-[12.5px] text-[#63635E] link-underline">Back to website</a>
        </form>
      </div>
    );
  }

  return <AdminConsole token={token} onLogout={onLogout} />;
}

function AdminConsole({ token, onLogout }) {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmCancel, setConfirmCancel] = useState(null); // booking obj
  const [notify, setNotify] = useState(true);
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const auth = { headers: { "X-Admin-Token": token } };

  const refresh = async () => {
    setLoading(true);
    try {
      const [b, f] = await Promise.all([
        axios.get(`${API}/admin/bookings`, auth),
        axios.get(`${API}/admin/feedback`, auth),
      ]);
      setBookings(b.data);
      setFeedback(f.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired — please sign in again.");
        onLogout();
        return;
      }
      toast.error("Couldn't load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelBooking = async () => {
    if (!confirmCancel || cancelling) return;
    setCancelling(true);
    try {
      await axios.delete(`${API}/admin/bookings/${confirmCancel.id}`, {
        ...auth,
        params: { notify, reason: reason || undefined },
      });
      toast.success(
        notify
          ? "Booking cancelled and notification sent."
          : "Booking cancelled (no email sent)."
      );
      setConfirmCancel(null);
      setReason("");
      setNotify(true);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't cancel the booking.");
    } finally {
      setCancelling(false);
    }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm("Delete this feedback message?")) return;
    try {
      await axios.delete(`${API}/admin/feedback/${id}`, auth);
      toast.success("Feedback deleted.");
      refresh();
    } catch (err) {
      toast.error("Couldn't delete.");
    }
  };

  const downloadCSV = (rows, columns, filename) => {
    const escape = (v) => {
      if (v == null) return "";
      const s = Array.isArray(v) ? v.join("; ") : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const head = columns.map((c) => c.label).join(",");
    const body = rows.map((r) => columns.map((c) => escape(r[c.key])).join(",")).join("\n");
    const blob = new Blob([head + "\n" + body], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const bookingCols = [
    { key: "created_at", label: "Created" },
    { key: "info_day_label", label: "Info Day" },
    { key: "time_slot", label: "Slot" },
    { key: "full_name", label: "Booker" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "party_size", label: "Party" },
    { key: "attendee_names", label: "Attendees" },
    { key: "topic", label: "Topic" },
    { key: "id", label: "Booking ID" },
  ];
  const feedbackCols = [
    { key: "created_at", label: "Created" },
    { key: "full_name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "subject", label: "Subject" },
    { key: "message", label: "Message" },
    { key: "id", label: "ID" },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="admin-console">
      <header className="bg-[#3A4B3A] text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#CBA365]">Admin</p>
            <p className="font-serif-display text-base tracking-tight">Loughrea Solar Farm</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-[12px] text-white/80 link-underline" data-testid="admin-back-to-site">View public site</a>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-sm border border-white/20 text-[12px] hover:bg-white/10 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => setTab("bookings")}
            data-testid="tab-bookings"
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-sm text-[13px] border transition-colors ${
              tab === "bookings"
                ? "bg-[#3A4B3A] text-white border-[#3A4B3A]"
                : "bg-white border-[#E0DCD3] text-[#2A2B2A] hover:border-[#8C9B86]"
            }`}
          >
            <Calendar className="h-3.5 w-3.5" /> Bookings ({bookings.length})
          </button>
          <button
            onClick={() => setTab("feedback")}
            data-testid="tab-feedback"
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-sm text-[13px] border transition-colors ${
              tab === "feedback"
                ? "bg-[#3A4B3A] text-white border-[#3A4B3A]"
                : "bg-white border-[#E0DCD3] text-[#2A2B2A] hover:border-[#8C9B86]"
            }`}
          >
            <MessageSquare className="h-3.5 w-3.5" /> Questions ({feedback.length})
          </button>

          <div className="ml-auto">
            <button
              onClick={() =>
                tab === "bookings"
                  ? downloadCSV(bookings, bookingCols, `bookings-${new Date().toISOString().slice(0, 10)}.csv`)
                  : downloadCSV(feedback, feedbackCols, `feedback-${new Date().toISOString().slice(0, 10)}.csv`)
              }
              data-testid="export-csv"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-[#4C4538] text-[#4C4538] text-[13px] font-medium hover:bg-[#4C4538] hover:text-white transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-[#E0DCD3] rounded-sm p-8 flex items-center gap-3 text-[#63635E]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : tab === "bookings" ? (
          <BookingsTable bookings={bookings} onCancel={setConfirmCancel} />
        ) : (
          <FeedbackTable feedback={feedback} onDelete={deleteFeedback} />
        )}
      </main>

      {confirmCancel && (
        <CancelModal
          booking={confirmCancel}
          notify={notify}
          setNotify={setNotify}
          reason={reason}
          setReason={setReason}
          cancelling={cancelling}
          onConfirm={cancelBooking}
          onClose={() => {
            setConfirmCancel(null);
            setReason("");
            setNotify(true);
          }}
        />
      )}
    </div>
  );
}

function BookingsTable({ bookings, onCancel }) {
  if (bookings.length === 0) {
    return (
      <div className="bg-white border border-[#E0DCD3] rounded-sm p-10 text-center text-[#63635E]" data-testid="bookings-empty">
        No bookings yet.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="bookings-list">
      {bookings.map((b) => (
        <article key={b.id} className="bg-white border border-[#E0DCD3] rounded-sm p-5 lg:p-6 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid={`booking-row-${b.id.slice(0, 8)}`}>
          <div className="md:col-span-4">
            <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#CBA365]">
              {b.info_day_label?.split("—")[0]?.trim()}
            </p>
            <p className="font-serif-display text-lg tracking-tight mt-1">{b.time_slot}</p>
            <p className="text-[12px] text-[#63635E] mt-1 font-mono">{b.id.slice(0, 8)}</p>
          </div>

          <div className="md:col-span-4">
            <p className="text-[13.5px] font-medium text-[#2A2B2A]">{b.full_name}</p>
            <p className="mt-1 text-[12.5px] text-[#63635E] flex items-center gap-1.5">
              <Mail className="h-3 w-3" />{" "}
              <a href={`mailto:${b.email}`} className="link-underline">{b.email}</a>
            </p>
            {b.phone && (
              <p className="mt-1 text-[12.5px] text-[#63635E] flex items-center gap-1.5">
                <Phone className="h-3 w-3" />{" "}
                <a href={`tel:${b.phone}`} className="link-underline">{b.phone}</a>
              </p>
            )}
            <p className="mt-2 text-[12px] text-[#4C4538]">
              <strong>{b.party_size}</strong> {b.party_size === 1 ? "person" : "people"}: {(b.attendee_names || []).join(", ")}
            </p>
          </div>

          <div className="md:col-span-3">
            {b.topic ? (
              <>
                <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#4C4538]">Topic</p>
                <p className="mt-1 text-[12.5px] text-[#2A2B2A] whitespace-pre-wrap">{b.topic}</p>
              </>
            ) : (
              <p className="text-[12.5px] text-[#63635E] italic">No topic added</p>
            )}
          </div>

          <div className="md:col-span-1 flex md:justify-end items-start">
            <button
              onClick={() => onCancel(b)}
              data-testid={`cancel-booking-${b.id.slice(0, 8)}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-[#A0341E] text-[#A0341E] text-[12px] font-medium hover:bg-[#A0341E] hover:text-white transition-colors"
            >
              <Trash2 className="h-3 w-3" /> Cancel
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}

function FeedbackTable({ feedback, onDelete }) {
  if (feedback.length === 0) {
    return (
      <div className="bg-white border border-[#E0DCD3] rounded-sm p-10 text-center text-[#63635E]" data-testid="feedback-empty">
        No questions yet.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="feedback-list">
      {feedback.map((f) => (
        <article key={f.id} className="bg-white border border-[#E0DCD3] rounded-sm p-5 lg:p-6 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid={`feedback-row-${f.id.slice(0, 8)}`}>
          <div className="md:col-span-3">
            <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#CBA365]">
              {new Date(f.created_at).toLocaleString("en-IE", { dateStyle: "medium", timeStyle: "short" })}
            </p>
            <p className="font-medium mt-1 text-[14px]">{f.full_name}</p>
            <a href={`mailto:${f.email}`} className="text-[12.5px] text-[#63635E] link-underline">{f.email}</a>
          </div>
          <div className="md:col-span-8">
            {f.subject && (
              <p className="text-[13px] font-semibold text-[#4C4538] mb-1">{f.subject}</p>
            )}
            <p className="text-[13px] text-[#2A2B2A] whitespace-pre-wrap leading-relaxed">{f.message}</p>
          </div>
          <div className="md:col-span-1 flex md:justify-end items-start">
            <button
              onClick={() => onDelete(f.id)}
              data-testid={`delete-feedback-${f.id.slice(0, 8)}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-[#A0341E] text-[#A0341E] text-[12px] font-medium hover:bg-[#A0341E] hover:text-white transition-colors"
            >
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}

function CancelModal({ booking, notify, setNotify, reason, setReason, cancelling, onConfirm, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-6"
      onClick={onClose}
      data-testid="cancel-modal"
    >
      <div
        className="bg-white rounded-sm max-w-md w-full p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="overline mb-2">Cancel booking</p>
        <h3 className="font-serif-display text-xl tracking-tight">Cancel {booking.full_name}'s slot?</h3>
        <p className="mt-3 text-[13.5px] text-[#63635E]">
          {booking.info_day_label?.split("—")[0]?.trim()} · <strong>{booking.time_slot}</strong> ·{" "}
          {booking.party_size} {booking.party_size === 1 ? "person" : "people"}
        </p>

        <label className="mt-5 flex items-start gap-2.5 cursor-pointer" data-testid="cancel-notify-toggle">
          <input
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            data-testid="cancel-notify"
            className="mt-0.5 h-4 w-4 accent-[#3A4B3A]"
          />
          <span className="text-[13px] text-[#2A2B2A]">
            Send a cancellation email to <strong>{booking.email}</strong>
          </span>
        </label>

        {notify && (
          <label className="block mt-4">
            <span className="block text-[11.5px] font-medium text-[#4C4538] mb-1">
              Optional note to include (will appear in the email)
            </span>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Slot was double-booked — we'd love to reschedule."
              data-testid="cancel-reason"
              className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3 py-2 text-[13px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30 resize-none"
            />
          </label>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="cancel-modal-close"
            className="h-10 px-4 rounded-sm border border-[#E0DCD3] text-[#2A2B2A] text-[13px] hover:bg-[#F2EFE8] transition-colors"
          >
            Keep booking
          </button>
          <button
            onClick={onConfirm}
            disabled={cancelling}
            data-testid="cancel-modal-confirm"
            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-sm bg-[#A0341E] text-white text-[13px] font-medium hover:bg-[#812919] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            {cancelling ? "Cancelling…" : "Cancel booking"}
          </button>
        </div>
      </div>
    </div>
  );
}
